import type { WebhookEvent } from "./webhooks.js";

/**
 * Klaviyo: Ereignisse aus Connect werden zu Klaviyo-Events (Metriken) am Profil des Endkunden. Der Händler baut damit
 * in Klaviyo eigene Abläufe (z. B. Versandbenachrichtigung im eigenen Design). Profil = Empfänger-E-Mail der Lieferadresse.
 */
export const KLAVIYO_METRICS: Partial<Record<WebhookEvent, string>> = {
  "order.shipped": "Logiful: Versendet",
  "shipment.out_for_delivery": "Logiful: In Zustellung",
  "shipment.pickup_ready": "Logiful: Zur Abholung bereit",
  "shipment.delivered": "Logiful: Zugestellt",
  "shipment.exception": "Logiful: Zustellproblem",
  "return.processed": "Logiful: Retoure geprüft",
  "order.delayed": "Logiful: Lieferverzögerung",
};
export const KLAVIYO_EVENT_KEYS = Object.keys(KLAVIYO_METRICS) as WebhookEvent[];

/** Gruppen für die Einrichtung (so wählt der Händler aus) */
export const KLAVIYO_EVENT_GROUPS: { key: string; label: string; hint: string; events: WebhookEvent[] }[] = [
  { key: "shipping", label: "Versand & Zustellung", hint: "Versendet, In Zustellung, Zur Abholung bereit, Zugestellt, Zustellproblem – Zustellstatus braucht das Modul Track & Trace", events: ["order.shipped", "shipment.out_for_delivery", "shipment.pickup_ready", "shipment.delivered", "shipment.exception"] },
  { key: "returns", label: "Retouren", hint: "Retoure im Lager geprüft – z. B. für „Deine Erstattung läuft“", events: ["return.processed"] },
  { key: "delay", label: "Lieferverzögerung", hint: "Auftrag wartet auf Ware – proaktiv informieren", events: ["order.delayed"] },
];

/** Klaviyo-API-Version (Kopfzeile „revision“) */
export const KLAVIYO_REVISION = "2024-10-15";

type Address = { email?: string; phone?: string; firstName?: string; lastName?: string; company?: string; country?: string; city?: string; zip?: string };
type OrderV1Like = {
  id: string;
  number: string;
  reference: string | null;
  channel: string;
  total: { amount_minor: number | null; currency: string };
  shipping_address: Address;
  lines: { sku: string; name: string; quantity: number }[];
  shipments: { carrier: string; tracking_numbers: string[]; tracking_urls: string[]; shipped_at: string }[];
  hold_reasons?: string[];
};
type ReturnV1Like = { id: string; order_number?: string | null; order?: { number?: string | null; shipping_address?: Address } | null; lines?: { sku: string | null; name?: string | null; quantity: number; condition?: string | null }[]; processed_at?: string | null; email?: string | null };

/**
 * Webhook-Nutzlast → Klaviyo-Event (JSON:API, POST /api/events/). null = nichts zu senden (z. B. keine E-Mail,
 * oder Zurückhaltung aus anderem Grund als fehlender Ware).
 */
export function klaviyoEventBody(payload: { id: string; event: string; created_at: string; data: Record<string, unknown> }): { body: unknown; skip?: undefined } | { body?: undefined; skip: string } {
  const metric = KLAVIYO_METRICS[payload.event as WebhookEvent];
  if (!metric) return { skip: "kein Klaviyo-Ereignis" };
  const order = payload.data.order as OrderV1Like | undefined;
  const ret = payload.data.return as ReturnV1Like | undefined;
  const delivery = payload.data.delivery as { tracking_number?: string; status?: string; status_text?: string | null; carrier?: string | null; tracking_url?: string | null } | undefined;
  const addr: Address = order?.shipping_address ?? ret?.order?.shipping_address ?? {};
  const email = (addr.email ?? ret?.email ?? "").trim();
  if (!email) return { skip: "keine E-Mail-Adresse beim Empfänger" };
  if (payload.event === "order.delayed" && !(order?.hold_reasons ?? []).some((r) => r.startsWith("Fehlbestand"))) return { skip: "keine Lieferverzögerung" };

  const ship = order?.shipments.at(-1);
  const tracking = delivery?.tracking_number ?? ship?.tracking_numbers[0] ?? null;
  const trackingUrl = delivery?.tracking_url ?? (tracking && ship ? ship.tracking_urls[ship.tracking_numbers.indexOf(tracking)] ?? ship.tracking_urls[0] : null) ?? null;
  const properties: Record<string, unknown> = {
    OrderNumber: order?.number ?? ret?.order?.number ?? ret?.order_number ?? null,
    ShopOrderNumber: order?.reference ?? null,
    Channel: order?.channel ?? null,
    ...(order ? { Items: order.lines.map((l) => ({ SKU: l.sku, Name: l.name, Quantity: l.quantity })), ItemNames: order.lines.map((l) => l.name) } : {}),
    ...(ret?.lines ? { ReturnedItems: ret.lines.map((l) => ({ SKU: l.sku, Name: l.name ?? l.sku ?? "", Quantity: l.quantity, Condition: l.condition ?? null })) } : {}),
    ...(tracking ? { TrackingNumber: tracking } : {}),
    ...(trackingUrl ? { TrackingUrl: trackingUrl } : {}),
    ...(delivery?.carrier ?? ship?.carrier ? { Carrier: delivery?.carrier ?? ship?.carrier } : {}),
    ...(delivery?.status_text ? { StatusText: delivery.status_text } : {}),
    ShippingCountry: addr.country ?? null,
    ShippingCity: addr.city ?? null,
  };
  const value = order?.total.amount_minor != null ? order.total.amount_minor / 100 : undefined;
  return {
    body: {
      data: {
        type: "event",
        attributes: {
          properties,
          time: payload.created_at,
          ...(value != null && payload.event === "order.shipped" ? { value, value_currency: order!.total.currency } : {}),
          // gleiche Zustellung nie doppelt zählen (Wiederholungen)
          unique_id: payload.id,
          metric: { data: { type: "metric", attributes: { name: metric } } },
          profile: {
            data: {
              type: "profile",
              attributes: {
                email,
                ...(addr.firstName ? { first_name: addr.firstName } : {}),
                ...(addr.lastName ? { last_name: addr.lastName } : {}),
                ...(addr.city || addr.country || addr.zip ? { location: { ...(addr.city ? { city: addr.city } : {}), ...(addr.country ? { country: addr.country } : {}), ...(addr.zip ? { zip: addr.zip } : {}) } } : {}),
              },
            },
          },
        },
      },
    },
  };
}
